import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plug, Loader2, ArrowRight, Database, Sparkles, ShieldCheck, X, Clock, Zap, Building2 } from "lucide-react";
import { Panel, Pill } from "./ui";
import { nexsysApi } from "./api";

const STATUS_UI = {
  not_connected: { label: "Not Connected", color: "#64748b" },
  requested: { label: "Requested", color: "#94a3b8" },
  provisioning: { label: "Provisioning", color: "#f59e0b" },
  ready: { label: "Ready", color: "#3b82f6" },
  connected: { label: "Connected", color: "#34d399" },
  error: { label: "Error", color: "#ef4444" },
};
const LIFECYCLE = ["requested", "provisioning", "ready", "connected", "error"];
const DIFF = { easy: "Quick setup", moderate: "Standard", advanced: "Enterprise" };

const Dot = ({ status, pulse }) => (
  <span
    className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${pulse ? "nx-pulse" : ""}`}
    style={{ background: (STATUS_UI[status] || STATUS_UI.not_connected).color, boxShadow: `0 0 8px ${(STATUS_UI[status] || STATUS_UI.not_connected).color}` }}
  />
);

function initials(name) {
  return name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

function ConnectorCard({ c, index, onConnect }) {
  const s = STATUS_UI[c.status] || STATUS_UI.not_connected;
  const governed = c.id === "servicenow";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] }}
      className="nx-panel nx-panel-hover nx-corners flex flex-col p-4"
      data-testid={`connector-${c.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-display text-sm font-bold text-black" style={{ background: c.accent, boxShadow: `0 0 18px ${c.accent}66` }}>
          {initials(c.name)}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-sm font-semibold text-white">{c.name}</h3>
          <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">{c.vendor} · {c.category}</div>
        </div>
        <div className="flex items-center gap-1.5"><Dot status={c.status} pulse={c.status === "provisioning"} /><span className="text-[11px]" style={{ color: s.color }}>{s.label}</span></div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {c.entities.map((e) => <span key={e} className="rounded-md border border-white/10 bg-black/20 px-2 py-0.5 text-[10px] text-white/60">{e}</span>)}
      </div>

      <div className="mt-3 flex items-center gap-2 text-[11px] text-white/45">
        <ShieldCheck className="h-3.5 w-3.5 text-white/35" /> {c.auth}
        <span className="ml-auto text-white/35">{DIFF[c.difficulty]}</span>
      </div>

      <button
        onClick={() => !governed && onConnect(c)}
        disabled={governed || c.status !== "not_connected"}
        className={`mt-4 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-semibold transition-colors ${
          governed || c.status !== "not_connected" ? "cursor-default border border-white/10 bg-white/[0.02] text-white/50" : "border border-white/10 bg-white/[0.03] text-white/85 hover:border-cyan-400/40 hover:text-white"
        }`}
        data-testid={`connect-${c.id}`}
      >
        <Plug className="h-4 w-4 text-cyan-300" />
        {governed ? (c.status === "connected" ? "Connected · governed" : `${s.label} · governed`) : c.status === "not_connected" ? "Connect" : s.label}
      </button>
      {governed && (
        <p className="mt-2 text-[10px] leading-snug text-white/35" data-testid="servicenow-governed-note">
          Real connector — status derives from a live one-incident read test plus a Governance-approved activation decision (Admin › Connectors). No request queue, no estimates.
        </p>
      )}
    </motion.div>
  );
}

function ConnectModal({ connector, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [busy, setBusy] = useState(false);
  if (!connector) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(connector, name.trim(), org.trim()); } finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[105] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="connect-modal">
      <motion.form initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} onSubmit={submit} onClick={(e) => e.stopPropagation()} className="nx-panel nx-corners w-full max-w-md p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-semibold text-white">Request {connector.name} connection</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-1 text-[12px] text-white/45">This logs a provisioning request and alerts the team. No credentials are collected yet.</p>
        <label className="mt-4 block text-[11px] uppercase tracking-wider text-white/40">Your name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" data-testid="connect-name" placeholder="e.g. John Doe" />
        <label className="mt-3 block text-[11px] uppercase tracking-wider text-white/40">Organization</label>
        <input value={org} onChange={(e) => setOrg(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" data-testid="connect-org" placeholder="e.g. Acme Construction" />
        <button type="submit" disabled={busy || !name.trim()} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 py-2.5 text-sm font-semibold text-black disabled:opacity-50" data-testid="connect-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Submit request
        </button>
      </motion.form>
    </motion.div>
  );
}

function AIEventModal({ event, onClose }) {
  if (!event) return null;
  const { ai_event: ai, connector, requested_by, organization } = event;
  const Row = ({ label, value }) => (
    <div className="flex items-start justify-between gap-4 border-b border-white/[0.06] py-2.5">
      <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">{label}</span>
      <span className="text-right text-[13px] font-medium text-white/90">{value}</span>
    </div>
  );
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[106] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="ai-event-modal">
      <motion.div initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }} onClick={(e) => e.stopPropagation()} className="nx-panel nx-corners w-full max-w-md overflow-hidden">
        <div className="flex items-center gap-2.5 border-b border-white/[0.06] bg-gradient-to-r from-cyan-500/15 to-indigo-500/15 px-5 py-4">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-indigo-500"><Sparkles className="h-4 w-4 text-black" /></span>
          <div><div className="font-mono text-[10px] tracking-widest text-cyan-300/80">AI EVENT</div><h3 className="font-display text-sm font-semibold text-white">{ai.headline}</h3></div>
          <button onClick={onClose} className="ml-auto rounded-lg p-1.5 text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-3">
          <Row label="Organization" value={organization || "—"} />
          <Row label="Connector" value={connector.name} />
          <Row label="Requested By" value={requested_by || "—"} />
          <div className="py-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">Recommended Next Step</div>
            <p className="mt-1 flex items-start gap-2 text-[13px] leading-relaxed text-white/85"><Zap className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />{ai.recommended_next_step}</p>
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2.5 text-[13px] text-white/80">
            <Clock className="h-4 w-4 text-cyan-300" /> Estimated setup time: <span className="font-semibold text-white">{ai.estimated_setup}</span>
          </div>
        </div>
        <div className="px-5 pb-5"><button onClick={onClose} className="w-full rounded-xl bg-cyan-500 py-2.5 text-sm font-semibold text-black" data-testid="ai-event-close">Got it</button></div>
      </motion.div>
    </motion.div>
  );
}

const SummaryTile = ({ label, count, color }) => (
  <div className="nx-panel nx-corners flex items-center gap-3 p-3.5">
    <span className="h-8 w-1 rounded-full" style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
    <div><div className="font-display text-2xl font-bold text-white">{count}</div><div className="font-mono text-[10px] uppercase tracking-wider text-white/45">{label}</div></div>
  </div>
);

export function Integrations() {
  const [data, setData] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [aiEvent, setAiEvent] = useState(null);

  const load = () => nexsysApi.integrations().then(setData).catch(() => setData({ connectors: [], pipeline: [], canonical: { entities: [] }, summary: {}, requests: [] }));
  useEffect(() => { load(); }, []);

  const submitRequest = async (connector, name, org) => {
    const res = await nexsysApi.requestIntegration({ connector_id: connector.id, requested_by: name, organization: org });
    setConnecting(null);
    setAiEvent({ ai_event: res.ai_event, connector, requested_by: name, organization: org });
    load();
  };
  const changeStatus = async (id, status) => { await nexsysApi.updateIntegrationStatus(id, status); load(); };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const sum = data?.summary || {};

  return (
    <div className="space-y-5" data-testid="integrations-view">
      <Panel title="Data Sources" subtitle="ENTERPRISE INTEGRATION FABRIC · INGEST · NORMALIZE · CORRELATE" right={<Pill tone="cyan"><Database className="h-3 w-3" /> {data ? `${data.connectors?.length || 0} systems` : "…"}</Pill>}>
        <p className="text-[13px] leading-relaxed text-white/55">
          NexSys unifies your enterprise delivery, service and finance systems into one canonical model — then correlates, predicts and prioritizes across all of them, surfacing recommendations through Chatty. Each connector is a pluggable adapter, ready to activate when you provide access.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="pipeline">
          {(data?.pipeline || []).map((s, i) => (
            <div key={s.key} className="flex items-center gap-2">
              <div className={`rounded-xl border px-3 py-2 ${s.status === "active" ? "border-cyan-400/40 bg-cyan-400/10" : "border-white/10 bg-white/[0.03]"}`}>
                <div className={`font-display text-xs font-semibold ${s.status === "active" ? "text-cyan-200" : "text-white/80"}`}>{s.title}</div>
                <div className="mt-0.5 max-w-[150px] text-[10px] leading-snug text-white/40">{s.desc}</div>
              </div>
              {i < (data?.pipeline?.length || 0) - 1 && <ArrowRight className="h-4 w-4 text-white/20" />}
            </div>
          ))}
        </div>
      </Panel>

      {/* Provisioning summary dashboard */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="provisioning-summary">
        <SummaryTile label="Pending" count={sum.requested || 0} color={STATUS_UI.requested.color} />
        <SummaryTile label="Provisioning" count={sum.provisioning || 0} color={STATUS_UI.provisioning.color} />
        <SummaryTile label="Ready" count={sum.ready || 0} color={STATUS_UI.ready.color} />
        <SummaryTile label="Connected" count={sum.connected || 0} color={STATUS_UI.connected.color} />
        <SummaryTile label="Failed" count={sum.error || 0} color={STATUS_UI.error.color} />
      </div>

      {/* Provisioning queue */}
      {data?.requests?.length > 0 && (
        <Panel title="Integration Provisioning Queue" subtitle="LIFECYCLE · UPDATE AS YOU PROVISION" right={<Pill tone="amber">{data.requests.length} in queue</Pill>}>
          <div className="space-y-2" data-testid="request-log">
            {data.requests.map((r) => (
              <div key={r.connector_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="request-row">
                <Dot status={r.status} pulse={r.status === "provisioning"} />
                <span className="font-display text-sm font-semibold text-white/90">{r.name}</span>
                <span className="flex items-center gap-1 text-[12px] text-white/50"><Building2 className="h-3.5 w-3.5" />{r.organization || "—"}</span>
                <span className="text-[12px] text-white/40">by {r.requested_by || "—"}</span>
                <span className="text-[11px] text-white/35">{fmt(r.requested_at)}</span>
                <select
                  value={r.status}
                  onChange={(e) => changeStatus(r.connector_id, e.target.value)}
                  className="ml-auto rounded-lg border border-white/10 bg-[#0b0f1c] px-2.5 py-1.5 text-[12px] text-white outline-none focus:border-cyan-400/50"
                  data-testid={`status-select-${r.connector_id}`}
                  style={{ color: STATUS_UI[r.status].color }}
                >
                  {LIFECYCLE.map((s) => <option key={s} value={s} style={{ color: "#fff" }}>{STATUS_UI[s].label}</option>)}
                </select>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Canonical model */}
      <Panel title="Canonical Model" subtitle="EVERY SOURCE NORMALIZES INTO THESE ENTITIES">
        <p className="mb-3 text-[12px] leading-relaxed text-white/50">{data?.canonical?.description}</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {(data?.canonical?.entities || []).map((e) => (
            <div key={e.key} className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="font-display text-sm font-semibold text-cyan-200">{e.label}</div>
              <div className="mt-0.5 text-[11px] leading-snug text-white/45">{e.desc}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Connectors — grouped by domain */}
      <div className="flex items-center gap-2 px-1">
        <Sparkles className="h-4 w-4 text-cyan-300" />
        <h3 className="font-display text-sm font-semibold text-white/90">Connectors</h3>
        {data && <span className="font-mono text-[11px] text-white/40">{data.connectors?.length || 0} systems</span>}
      </div>
      {!data && <div className="flex items-center gap-3 py-8 text-white/50"><Loader2 className="h-5 w-5 animate-spin text-cyan-300" /> Loading connectors…</div>}
      {data && (() => {
        const groups = data.groups && data.groups.length ? data.groups : [...new Set((data.connectors || []).map((c) => c.group || "Other"))];
        return groups.map((g) => {
          const list = (data.connectors || []).filter((c) => (c.group || "Other") === g);
          if (!list.length) return null;
          return (
            <div key={g} className="space-y-3" data-testid={`connector-group-${g}`}>
              <div className="flex items-center gap-3 px-1 pt-1">
                <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300/80">{g}</h4>
                <span className="text-[11px] text-white/30">{list.length}</span>
                <span className="h-px flex-1 bg-white/[0.06]" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((c, i) => <ConnectorCard key={c.id} c={c} index={i} onConnect={setConnecting} />)}
              </div>
            </div>
          );
        });
      })()}

      <AnimatePresence>
        {connecting && <ConnectModal connector={connecting} onClose={() => setConnecting(null)} onSubmit={submitRequest} />}
        {aiEvent && <AIEventModal event={aiEvent} onClose={() => setAiEvent(null)} />}
      </AnimatePresence>
    </div>
  );
}
